import React, { useRef, useState } from 'react';
import { hexToBytes } from '@stacks/common';
import {
  ContractCallOptions,
  ContractDeployOptions,
  FinishedTxPayload,
  SponsoredFinishedTxPayload,
  STXTransferOptions,
  TransactionPayload,
} from '@stacks/connect';
import { StacksNetwork } from '@stacks/network';
import {
  addressToString,
  AnchorMode,
  BytesReader,
  deserializeCV,
  deserializePostCondition,
  PostCondition,
  PostConditionMode,
  StacksTransaction,
  UnsignedMultiSigContractDeployOptions,
} from '@stacks/transactions';
import { deserializeTransaction } from '@stacks/transactions';
import { generateSecretKey, generateWallet, makeAuthResponse } from '@stacks/wallet-sdk';
import { createSafeTransaction, getTransactionById } from 'api';
import BigNumber from 'bignumber.js';
import useAddress from 'hooks/use-address';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import useSign from 'hooks/use-sign';
import { useWalletProvider } from 'hooks/useWalletProvider';
import useData, { PROVIDERS } from 'hooks/useData';
import { decodeToken, Json, TokenSigner } from 'jsontokens';
import { SafeTxError, safeTxError } from 'types';
import { DecodedAuthRequest } from 'types/asigna';
import {
  generateUnsignedContractCall,
  generateUnsignedContractDeploy,
  generateUnsignedStxTransfer,
} from 'utils/transaction-create';
import { v4 as uuidv4 } from 'uuid';

import { NonceDialog } from '../NonceDialog';

interface DecodedAuthTokenPayload extends Record<string, Json> {
  associationToken: string | null;
  hubUrl: string | null;
  profile_url: string | null;
  profile: {
    stxAddress: {
      testnet: string;
      mainnet: string;
    };
  };
}

type TransactionPayloadWithTxType = TransactionPayload & {
  txType?: string;
  payloadType?: number;
  contract?: string;
  functionArgs?: any[];
  functionName?: string;
  amount?: string;
};

function hexToBuff(hex: string): Buffer {
  return Buffer.from(cleanHex(hex), 'hex');
}

interface IframeProps {
  url: string;
}

const source = 'asigna-stx';

type IframeMessage = {
  source: typeof source;
  data: InitData | TxData | MsgData;
};

type InitData = string;

type TxData = string;

type MsgData = string;

const mapFunctionArgs = (callOptions: ContractCallOptions) =>
  callOptions.functionArgs.map((arg) => {
    if (typeof arg === 'string') return deserializeCV(hexToBuff(arg as string));

    return arg;
  });

function modifyAuthResponseToken(
  authResponse: string,
  safeAddress: string,
  dataPrivateKey: string
) {
  const decodedToken = decodeToken(authResponse).payload as unknown as DecodedAuthTokenPayload;
  decodedToken.associationToken = null;
  decodedToken.hubUrl = null;
  decodedToken.profile_url = null;
  decodedToken.profile.stxAddress.testnet = safeAddress;
  decodedToken.profile.stxAddress.mainnet = safeAddress;
  const changedTokenSigner = new TokenSigner('ES256k', dataPrivateKey);
  const token = changedTokenSigner.sign(decodedToken as Json);

  return token;
}

function cleanHex(hexWithMaybePrefix: string): string {
  if (hexWithMaybePrefix !== 'string') return hexWithMaybePrefix;

  return hexWithMaybePrefix.startsWith('0x')
    ? hexWithMaybePrefix.replace('0x', '')
    : hexWithMaybePrefix;
}

function convertContractAddress(address: string | ContractAddressObject): string {
  if (typeof address === 'object' && address !== null) {
    return addressToString({
      type: address.type,
      version: address.version,
      hash160: address.hash160,
    });
  }

  return address;
}

function handlePostConditionMode(postConditionMode: string | PostConditionMode | undefined) {
  if (typeof postConditionMode === 'string') {
    if (postConditionMode === 'allow') return PostConditionMode.Allow;
    else return PostConditionMode.Deny;
  }

  return postConditionMode;
}

interface ContractAddressObject {
  hash160: string;
  type: number;
  version: number;
}

interface ContractNameObject {
  content: string;
  lengthPrefixBytes: number;
  maxLengthBytes: number;
  type: number;
}

function convertName(name: string | ContractNameObject): string {
  if (typeof name === 'object' && name !== null) {
    return name.content;
  }

  return name;
}

function postConditionFromString(postCondition: string): PostCondition {
  const reader = new BytesReader(hexToBytes(postCondition));

  return deserializePostCondition(reader);
}

const isContractCallPayload = (txPayload: TransactionPayload): boolean => {
  const payload = txPayload as TransactionPayloadWithTxType;

  return Boolean(
    payload.txType === 'contract_call' ||
    payload.payloadType === 2 ||
    (payload.contract && payload.functionArgs && payload.functionName)
  );
};

const isTransaferPayload = (txPayload: TransactionPayload): boolean => {
  const payload = txPayload as TransactionPayloadWithTxType;

  return Boolean(
    payload.txType === 'token_transfer' || payload.payloadType === 0 || payload.amount
  );
};

function handlePostConditions(postConditions: any): PostCondition[] {
  return postConditions.map((postCondition: string | PostCondition) => {
    if (typeof postCondition === 'string') return postConditionFromString(postCondition);

    return postCondition;
  });
}

async function generateAccount() {
  const password = 'password';
  const secretKey = generateSecretKey();

  const wallet = await generateWallet({
    secretKey,
    password,
  });

  return wallet.accounts[0];
}

export const Iframe: React.FC<IframeProps> = ({ url }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [isLoad, setLoad] = useState(false);
  const { safe } = useSafe();
  const [step, setStep] = useState(0);
  const nonceRef = useRef(-1);
  const feeRef = useRef(-1);
  const promiseRefResolve = useRef<(value: void | PromiseLike<void>) => void>();
  const promiseRef = useRef<Promise<void>>();
  const [network] = useNetwork();
  const { signTx } = useSign();
  const walletProvider = useWalletProvider();
  const txRef = useRef('');
  const [isFeeSet, setFeeSet] = useState(false);
  const { signMessage } = useData();

  const getAuthMsg = () => {
    const msg: IframeMessage = {
      source,
      data: '',
    };

    return msg;
  };

  const walletAccount = useAddress();

  const generateTx = async (
    txPayload: TransactionPayload,
    method: string,
    desirializedTx?: StacksTransaction,
    nonceOverride?: number,
    fee?: number
  ) => {
    const nonce = nonceOverride || nonceRef.current;
    const callOptions = txPayload as ContractCallOptions;
    if (isTransaferPayload(txPayload)) {
      const transferOptions = txPayload as STXTransferOptions;

      return await generateUnsignedStxTransfer(
        nonce,
        transferOptions.recipient || '',
        BigNumber(transferOptions.amount as string).toNumber(),
        StacksNetwork.fromName(network),
        safe.owners.map((owner) => owner.publicKey),
        safe.threshold,
        safe.address,
        fee,
        ''
      );
    }

    const usedPostConditions = callOptions.postConditions || desirializedTx?.postConditions;
    const usedMode = callOptions.postConditionMode || desirializedTx?.postConditionMode;
    const finalPostConditions = usedPostConditions
      ? handlePostConditions(
          method === 'stx_signTransaction' ? usedPostConditions.values : usedPostConditions
        )
      : undefined;

    if (isContractCallPayload(txPayload)) {
      const [contractAddress, contractName] =
        'contract' in callOptions && typeof callOptions.contract === 'string'
          ? callOptions.contract.split('.')
          : [];

      const finalContractAddress = convertContractAddress(
        contractAddress || callOptions.contractAddress
      );
      const finalContractName = convertName(contractName || callOptions.contractName);
      const finalFunctionName = convertName(callOptions.functionName);

      return await generateUnsignedContractCall(
        nonce,
        finalContractAddress,
        finalContractName,
        finalFunctionName,
        //@ts-expect-error correct type
        mapFunctionArgs(callOptions),
        StacksNetwork.fromName(network),
        safe.owners.map((o) => o.publicKey),
        safe.threshold,
        finalPostConditions,
        undefined,
        Number(callOptions.fee) || 3000,
        handlePostConditionMode(usedMode),
        callOptions.anchorMode
      );
    } else {
      const deployOptions = txPayload as ContractDeployOptions;
      const options: UnsignedMultiSigContractDeployOptions = {
        // @ts-expect-error correct type
        contractName: deployOptions.contractName || deployOptions.name,
        // @ts-expect-error correct type
        codeBody: deployOptions.codeBody || deployOptions.clarityCode,
        nonce,
        anchorMode: deployOptions.anchorMode ?? AnchorMode.Any,
        postConditionMode: handlePostConditionMode(usedMode),
        postConditions: finalPostConditions,
        network,
        publicKeys: safe.owners.map((owner) => owner.publicKey),
        numSignatures: safe.threshold,
        useNonSequentialMultiSig: true,
        fee: Number(deployOptions.fee) || 3000,
        clarityVersion: deployOptions.clarityVersion,
      };

      return await generateUnsignedContractDeploy(options);
    }
  };

  const onLoad = () => {
    setLoad(true);
    if (!iframeRef.current || !walletAccount) return;
    const msg = getAuthMsg();
    iframeRef.current.contentWindow?.postMessage(msg, '*');
    window.onmessage = async (e) => {
      if (e.data.source === source && e.data.auth) {
        iframeRef.current?.contentWindow?.postMessage(
          {
            data: {
              safeInfo: {
                address: safe.address,
                threshold: safe.threshold,
                users: safe.owners,
                publicKey: '',
              },
            },
            source,
          },
          '*'
        );
      }
      if (e.data.source === source && e.data.authenticationRequest) {
        const { payload } = decodeToken(e.data.authenticationRequest);
        const decodedAuthRequest = payload as unknown as DecodedAuthRequest;
        const account = await generateAccount();
        const authResponse = await makeAuthResponse({
          gaiaHubUrl: 'https://hub.blockstack.org',
          appDomain: url,
          transitPublicKey: decodedAuthRequest.public_keys[0],
          scopes: ['store_write'],
          account,
        });

        const modifiedAuthResponse = modifyAuthResponseToken(
          authResponse,
          safe.address,
          account.dataPrivateKey
        );
        iframeRef.current?.contentWindow?.postMessage(
          {
            authenticationRequest: modifiedAuthResponse,
            source,
          },
          '*'
        );
      } else if (e.data.source === source && e.data.transactionInfo) {
        const txInfo = (await getTransactionById(e.data.transactionInfo)).transaction;
        const msg = {
          source,
          data: { transactionInfo: txInfo },
        };
        iframeRef.current?.contentWindow?.postMessage(msg, '*');
      } else if (e.data.source === source && e.data.transactionRequest) {
        const { payload } = decodeToken(e.data.transactionRequest);
        const type = e.data.noExecute ? 'SIGNATURE' : 'TRANSACTION';
        const txPayload: TransactionPayload =
          typeof payload === 'string'
            ? (deserializeTransaction(new BytesReader(Buffer.from(payload, 'hex')))
                .payload as unknown as TransactionPayload)
            : (payload as unknown as TransactionPayload);
        const callPayload = txPayload as unknown as ContractCallOptions;
        const fnArgs = callPayload.functionArgs?.map((arg) => {
          if (typeof arg === 'string') return deserializeCV(hexToBuff(arg as string));
          if (arg.type === 12) {
            Object.keys(arg.data).forEach((key) => {
              // @ts-expect-error correct type
              const buf = arg.data[key].buffer;
              if (buf) {
                // @ts-expect-error correct type
                arg.data[key].buffer = Buffer.from(Object.values(buf));
              }
            });
          }
          if (arg.type === 2) {
            // @ts-expect-error correct type
            if (arg.buffer) arg.buffer = Buffer.from(Object.values(arg.buffer));
          }

          return arg;
        });
        if (callPayload.functionArgs) {
          callPayload.functionArgs = fnArgs;
        }
        promiseRef.current = new Promise((res) => {
          promiseRefResolve.current = res;
        });
        // @ts-expect-error correct type
        const feeTx = txPayload.fee;
        if (!Boolean(feeTx)) {
          txRef.current = await generateTx(txPayload, '', undefined, 1);
        }
        setStep(1);
        setFeeSet(Boolean(feeTx));
        feeRef.current = Number(feeTx);
        await promiseRef.current;
        const nonce = nonceRef.current;
        if (isTransaferPayload(txPayload)) {
          const transferOptions = txPayload as STXTransferOptions;
          const tx = await generateUnsignedStxTransfer(
            nonce,
            walletAccount,
            BigNumber(transferOptions.amount as string).toNumber(),
            StacksNetwork.fromName(network),
            safe.owners.map((owner) => owner.publicKey),
            safe.threshold,
            safe.address,
            feeRef.current,
            ''
          );
          const afterSign = async (signedTx?: string) => {
            const response = await createSafeTransaction(safe._id, tx, signedTx, undefined, type);
            const responsePayload: FinishedTxPayload | SponsoredFinishedTxPayload = {
              txRaw: tx,
              // @ts-expect-error todo: fix this
              multisigTxId: response.data._id,
              txId: '',
            };
            iframeRef.current?.contentWindow?.postMessage(
              {
                transactionRequest: responsePayload,
                source,
              },
              '*'
            );

            return safeTxError(SafeTxError.UnspecifiedError);
          };
          signTx(tx, afterSign);
        } else if (isContractCallPayload(txPayload)) {
          const [contractAddress, contractName] =
            'contract' in txPayload && typeof txPayload.contract === 'string'
              ? txPayload.contract.split('.')
              : [];

          const finalContractAddress = convertContractAddress(
            contractAddress || callPayload.contractAddress
          );
          const finalContractName = convertName(contractName || callPayload.contractName);
          const finalFunctionName = convertName(callPayload.functionName);

          const tx = await generateUnsignedContractCall(
            nonce,
            finalContractAddress,
            finalContractName,
            finalFunctionName,
            //@ts-expect-error correct type
            mapFunctionArgs(callPayload),
            StacksNetwork.fromName(network),
            safe.owners.map((o) => o.publicKey),
            safe.threshold,
            callPayload.postConditions
              ? handlePostConditions((callPayload.postConditions as string[]) || [])
              : undefined,
            undefined,
            feeRef.current,
            handlePostConditionMode(callPayload.postConditionMode),
            callPayload.anchorMode
          );

          const afterSign = async (signedTx?: string) => {
            const response = await createSafeTransaction(safe._id, tx, signedTx, undefined, type);

            // if (response.success) return SAFE_TX_SUCCESS;

            const responsePayload: FinishedTxPayload | SponsoredFinishedTxPayload = {
              txRaw: tx,
              // @ts-expect-error coorect type

              txId: response.data._id,
            };
            iframeRef.current?.contentWindow?.postMessage(
              {
                transactionRequest: responsePayload,
                source,
              },
              '*'
            );

            return safeTxError(SafeTxError.UnspecifiedError);
          };
          signTx(tx, afterSign);
        } else {
          const deployOptions = txPayload as ContractDeployOptions;
          const options: UnsignedMultiSigContractDeployOptions = {
            // @ts-expect-error correct type
            contractName: deployOptions.contractName || deployOptions.name,
            // @ts-expect-error correct type
            codeBody: deployOptions.codeBody || deployOptions.clarityCode,
            nonce,
            anchorMode: deployOptions.anchorMode ?? AnchorMode.Any,
            postConditionMode: handlePostConditionMode(deployOptions.postConditionMode),
            postConditions: handlePostConditions((deployOptions.postConditions as string[]) || []),
            network,
            publicKeys: safe.owners.map((owner) => owner.publicKey),
            numSignatures: safe.threshold,
            useNonSequentialMultiSig: true,
            fee: feeRef.current,
            clarityVersion: deployOptions.clarityVersion,
          };
          const afterSign = async (signedTx?: string) => {
            const response = await createSafeTransaction(safe._id, tx, signedTx, undefined, type);

            // if (response.success) return SAFE_TX_SUCCESS;
            const responsePayload: FinishedTxPayload | SponsoredFinishedTxPayload = {
              txRaw: tx,
              // @ts-expect-error coorect type

              txId: response.data._id,
            };
            iframeRef.current?.contentWindow?.postMessage(
              {
                transactionRequest: responsePayload,
                source,
              },
              '*'
            );

            return safeTxError(SafeTxError.UnspecifiedError);
          };

          const tx = await generateUnsignedContractDeploy(options);
          signTx(tx, afterSign);
        }
      } else if (e.data.source === source) {
        const { data } = e;
        const method = Object.keys(data).filter((key) => key !== 'source')[0];
        const requestId = data[method]?.id ?? uuidv4();
        switch (method) {
          case 'getAddresses':
          case 'stx_getAddresses':
            iframeRef.current?.contentWindow?.postMessage(
              {
                source,
                [method]: {
                  result: {
                    addresses: [{ symbol: 'STX', address: safe.address, publicKey: '' }],
                  },
                  id: requestId,
                  jsonrpc: '2.0',
                },
              },
              '*'
            );
            break;
          case 'stx_callContract':
          case 'stx_deployContract':
          case 'stx_transferStx':
          case 'stx_transferFungibleToken':
          case 'stx_transferNonFungibleToken':
          case 'stx_signTransaction':
            const txPayload = data[method].transaction
              ? (deserializeTransaction(
                  new BytesReader(Buffer.from(data[method].transaction, 'hex'))
                ).payload as unknown as TransactionPayload)
              : (data[method] as TransactionPayload);
            console.error('txPayload', txPayload);
            const desirializedTx = data[method].transaction
              ? deserializeTransaction(
                  new BytesReader(Buffer.from(data[method].transaction, 'hex'))
                )
              : undefined;
            // @ts-expect-error correct type
            const txFee = txPayload.fee;
            const type = 'TRANSACTION';
            promiseRef.current = new Promise((res) => {
              promiseRefResolve.current = res;
            });

            if (!Boolean(txFee)) {
              txRef.current = await generateTx(txPayload, method, desirializedTx, 1);
            }

            setStep(1);
            setFeeSet(Boolean(txFee));
            feeRef.current = Number(txFee);
            await promiseRef.current;
            const nonce = nonceRef.current;

            // TODO: fix this

            let tx = '';
            const usedPostConditions = txPayload.postConditions || desirializedTx?.postConditions;
            const usedMode =
              (txPayload as ContractCallOptions).postConditionMode ||
              desirializedTx?.postConditionMode;
            const finalPostConditions = usedPostConditions
              ? handlePostConditions(
                  method === 'stx_signTransaction' ? usedPostConditions.values : usedPostConditions
                )
              : undefined;
            if (
              method === 'stx_transferStx' ||
              method === 'stx_transferFungibleToken' ||
              method === 'stx_transferNonFungibleToken' ||
              isTransaferPayload(txPayload)
            ) {
              const transferOptions = txPayload as STXTransferOptions;
              tx = await generateUnsignedStxTransfer(
                nonce,
                transferOptions.recipient,
                BigNumber(transferOptions.amount as string).toNumber(),
                StacksNetwork.fromName(network),
                safe.owners.map((owner) => owner.publicKey),
                safe.threshold,
                safe.address,
                feeRef.current,
                ''
              );
            } else if (method === 'stx_callContract' || isContractCallPayload(txPayload)) {
              const [contractAddress, contractName] =
                'contract' in txPayload && typeof txPayload.contract === 'string'
                  ? txPayload.contract.split('.')
                  : [];
              const callOptions = txPayload as ContractCallOptions;

              const finalContractAddress = convertContractAddress(
                contractAddress || callOptions.contractAddress
              );
              const finalContractName = convertName(contractName || callOptions.contractName);
              const finalFunctionName = convertName(callOptions.functionName);

              tx = await generateUnsignedContractCall(
                nonce,
                finalContractAddress,
                finalContractName,
                finalFunctionName,
                //@ts-expect-error correct type
                mapFunctionArgs(callOptions),
                StacksNetwork.fromName(network),
                safe.owners.map((o) => o.publicKey),
                safe.threshold,
                finalPostConditions,
                undefined,
                feeRef.current,
                handlePostConditionMode(usedMode),
                callOptions.anchorMode
              );
            } else if (method === 'stx_deployContract' || method === 'stx_signTransaction') {
              const deployOptions = txPayload as ContractDeployOptions;
              const options: UnsignedMultiSigContractDeployOptions = {
                // @ts-expect-error correct type
                contractName: deployOptions.contractName || deployOptions.name,
                // @ts-expect-error correct type
                codeBody: deployOptions.codeBody || deployOptions.clarityCode,
                nonce,
                anchorMode: deployOptions.anchorMode ?? AnchorMode.Any,
                postConditionMode: handlePostConditionMode(usedMode),
                postConditions: finalPostConditions,
                network,
                publicKeys: safe.owners.map((owner) => owner.publicKey),
                numSignatures: safe.threshold,
                useNonSequentialMultiSig: true,
                fee: feeRef.current,
                clarityVersion: deployOptions.clarityVersion,
              };
              tx = await generateUnsignedContractDeploy(options);
            }

            const afterSign = async (signedTx?: string) => {
              const response = await createSafeTransaction(safe._id, tx, signedTx, undefined, type);
              iframeRef.current?.contentWindow?.postMessage(
                {
                  source,
                  [method]: {
                    result: {
                      // @ts-expect-error todo: fix this
                      multisigTxId: response.data._id,
                      txId: '',
                    },
                    id: requestId,
                    jsonrpc: '2.0',
                  },
                },
                '*'
              );

              return safeTxError(SafeTxError.UnspecifiedError);
            };
            signTx(tx, afterSign);
            break;
          case 'stx_signMessage':
            const messageData = data[method] as { message: string };
            signMessage(walletProvider?.provider ?? PROVIDERS.Leather, messageData.message, safe.address)
              .then((result) => {
                iframeRef.current?.contentWindow?.postMessage(
                  {
                    source,
                    [method]: {
                      result: {
                        publicKey: result.publicKey,
                        signature: result.signature,
                      },
                      id: requestId,
                      jsonrpc: '2.0',
                    },
                  },
                  '*'
                );
              })
              .catch((error) => {
                console.error('Error signing message:', error);
                iframeRef.current?.contentWindow?.postMessage(
                  {
                    source,
                    [method]: {
                      error: {
                        message: error.toString(),
                        code: -32000,
                      },
                      id: requestId,
                      jsonrpc: '2.0',
                    },
                  },
                  '*'
                );
              });
            break;
          default:
            console.error(`Unsupported method: ${method}`);
            iframeRef.current?.contentWindow?.postMessage(
              {
                source,
                [method]: {
                  error: {
                    code: -32601,
                    message: `Method not found: ${method}`,
                  },
                  id: requestId,
                  jsonrpc: '2.0',
                },
              },
              '*'
            );
        }
      }
    };
  };

  return (
    <>
      {step === 1 && (
        <NonceDialog
          isFee={isFeeSet}
          onClose={() => {
            setStep(0);
            promiseRefResolve.current = undefined;
            promiseRef.current = undefined;
          }}
          onSuccess={(val, fee) => {
            if (fee) feeRef.current = fee;
            nonceRef.current = val;
            promiseRefResolve.current?.();
            setStep(0);
          }}
          transactionHex={txRef.current}
        />
      )}
      {!isLoad && (
        <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-evenly z-0">
          <img src="/loading-gif.gif" width={32} height={32} alt="loading" />
        </div>
      )}
      <iframe
        src={url}
        ref={iframeRef}
        width="100%"
        height="100%"
        onLoad={onLoad}
        title="apps"
        style={{ border: 0, height: '100vh' }}
      />
    </>
  );
};
